import { Card, Carousel } from 'antd';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Image, Typography } from 'tfc-components';

import NotFoundBlog from './NotFoundBlog';
import { mapBlogData } from './helper';

import backgroundBanner from 'assets/images/home/bg-banner1.png';
import banner from 'assets/images/home/bg-blog-detail.png';
import chatConclusion from 'assets/images/home/blog-bg-conclusion-chat.png';
import bgConclusion from 'assets/images/home/blog-bg-conclusion.png';
import bgFrameProblem from 'assets/images/home/blog-frame-problem.png';
import bgFrameSolution from 'assets/images/home/blog-frame-solution.png';
import extendsStoryHook from 'assets/images/home/extends-bg-story-hook.png';
import extendsStoryHooks from 'assets/images/home/extends-bg-story-hooks.png';
import extendsBlog2 from 'assets/images/home/extends-problem.png';
import feature_2_extend from 'assets/images/home/feature_2_extend.png';
import Loading from 'components/atoms/Loading';
import Banner from 'components/organisms/Banner';
import Section from 'components/organisms/Section';
import useDeviceQueries from 'hooks/useDeviceQueries';
import ModalRegister from 'pages/Home/ModalRegister';
import FreeTrial from 'pages/PricingPlan/FreeTrialModal';
import { getBannerService, getNewDetailService, getPreviewNewsService } from 'services/news';
import { AgentSection, BannerItem } from 'services/news/type';
import mapModifiers from 'utils/functions';

interface CompareProps {
  data?: AgentSection;
}

const getResponsiveImage = (
  isDesktop: boolean,
  isTablet: boolean,
  isMobile: boolean,
  bannerData?: BannerItem,
): string => {
  if (!bannerData) return backgroundBanner;
  if (isMobile) return bannerData.data.imageMobile || bannerData.data.imageDesktop || '';
  if (isTablet) return bannerData.data.imageTabLet || bannerData.data.imageMobile || bannerData.data.imageDesktop || '';
  if (isDesktop) return bannerData.data.imageDesktop || '';
  return bannerData.data.imageDesktop || '';
};

const getFrameBg = (index: number, innerImg: string) => {
  const frameIndex = index % 3;

  switch (frameIndex) {
    case 0:
      return (
        <div className="p-blog_imageContainer">
          <Image extendClasses="p-blog_image" imgSrc={bgFrameProblem} alt="Google" ratio={607 / 398} />
          <div className="p-blog_innerImageExtendWrap p-blog_innerImageExtendWrap-problemSection">
            <Image imgSrc={extendsBlog2} alt="extend" extendClasses="p-blog_innerImageExtend" ratio={224 / 187} />
          </div>
          <div className="p-blog_innerImageWrap">
            <img src={innerImg} alt="Inner" className="p-blog_innerImage" />
          </div>
        </div>
      );
    case 1:
      return (
        <div className="p-blog_imageContainer">
          <Image extendClasses="p-blog_image" imgSrc={bgFrameSolution} alt="Google" ratio={607 / 398} />
          <div className="p-blog_innerImageExtendWrap p-blog_innerImageExtendWrap-solutionSection">
            <Image imgSrc={feature_2_extend} alt="extend" extendClasses="p-blog_innerImageExtend" ratio={224 / 187} />
          </div>
          <div className="p-blog_innerImageWrap">
            <img src={innerImg} alt="Inner" className="p-blog_innerImage" />
          </div>
        </div>
      );
    default:
      return (
        <div className="p-blog_imageContainer">
          <Image extendClasses="p-blog_image" imgSrc={bgFrameProblem} alt="Google" ratio={590 / 414} />
          <div className="p-blog_innerImageWrap">
            <img src={innerImg} alt="Inner" className="p-blog_innerImage" />
          </div>
        </div>
      );
  }
};

const CompareSection: React.FC<CompareProps> = ({ data }) => {
  const [active, setActive] = React.useState(0);

  return (
    <>
      <Typography.Heading type="h2" extendClasses="p-home_agentSection_title f-36x48" fontweight="600">
        {data?.title}
      </Typography.Heading>
      <div className="p-blog_agentSection p-home_agentSection_wrap">
        {data?.items.map((item, index) => (
          <div key={`${item.title}-${index.toString()}`} className="p-blog_agentSection_itemWrap p-home_agentSection_itemWrap">
            <div className={mapModifiers('p-home_agentSection_item', active === index && 'active')}>
              <div className="p-home_agentSection_title" onClick={() => setActive(index)}>
                <Typography.Text
                  fontweight="600"
                  extendClasses="f-18x24"
                >
                  {item.title}
                </Typography.Text>
              </div>
              {/* <Typography.Text fontweight="600" extendClasses="p-home_agentSection_text">
                    Your internal AI copilot.
                  </Typography.Text> */}
              {/* <Typography.Text extendClasses="p-home_agentSection_text">
                {item.description}
              </Typography.Text> */}
              <div
                className="p-home_agentSection_text p-blog_storyHook_description"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />

            </div>
            <div className={`${mapModifiers('p-blog_agentSection_imageWrap p-home_agentSection', active !== index && 'hidden')}`}>
              <img className="p-blog_agentSection_image" src={item.imgSrc} alt={item.imgAlt} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

function truncate(str: string, n: number) {
  return str.length > n ? `${str.substr(0, n - 1)}...` : str;
}

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewCode = searchParams.get('preview');
  const isPreview = Boolean(previewCode);
  const localeParams = searchParams.get('locale') || 'vi';
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenFreeModal, setIsOpenFreeModal] = useState(false);
  const { isDesktop, isMobile, isTablet } = useDeviceQueries();

  const { data: blog, isLoading, isError } = useQuery(
    {
      queryKey: ['newsById', slug],
      queryFn: () => {
        if (!slug) return;
        if (isPreview) {
          // eslint-disable-next-line consistent-return
          return getPreviewNewsService({
            endPoint: 'blogs',
            previewCode: previewCode!,
            locale: localeParams,
          });
        }
        // eslint-disable-next-line consistent-return
        return getNewDetailService({
          slug,
          locale: localeParams,
          endPoint: 'blogs',
        });
      },
      enabled: !!slug || isPreview,
      select: (data) => mapBlogData(data, localeParams)
    }
  );

  const { data: bannerData, isLoading: isLoadingBanner } = useQuery({
    queryKey: ['banner', blog?.bannerId],
    queryFn: () => getBannerService(blog?.bannerId || 0),
    enabled: !!blog?.bannerId,
    select: (data) => data,
  });

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const pathName = window.location.pathname;
  React.useEffect(() => {
    const scriptId = 'evol-chatbot-script';

    const cleanupChatbot = () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }

      // Remove specific iframe by ID from the script
      const scriptIframe = document.getElementById('myIframe');
      if (scriptIframe) {
        scriptIframe.remove();
      }

      // Remove common widget UI containers
      const widgetSelectors = [
        '#chatbot-widget-container',
        '.chatbot-widget-container',
        '#evol-chatbot-container',
        'iframe[src*="chatbot-widget"]',
      ];

      widgetSelectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => el.remove());
      });

      // Clear the duplicate guard used in the script
      if ((window as any).ChatbotWidget) {
        delete (window as any).ChatbotWidget;
      }

      // Clear any other global state
      if ((window as any).EvolChatbot) {
        delete (window as any).EvolChatbot;
      }
    };

    if (blog?.botKey) {
      // First, clean up any existing script/widget before a fresh start
      cleanupChatbot();

      // Create and inject new script
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/javascript';
      script.src = 'https://ask2auto.com/chatbot-widget-pro.js?v=1';
      script.async = true;
      script.setAttribute('data-env', 'prod');
      script.setAttribute('data-api-key', blog.botKey);
      document.head.appendChild(script);

      return () => cleanupChatbot();
    }

    // If no botKey, ensure cleanup
    cleanupChatbot();
    return undefined;
  }, [blog?.botKey, slug]);

  if (isError) {
    return (
      <NotFoundBlog />
    );
  }
  return isLoading ? <Loading variant="fullscreen" width={50} /> : (
    <div className="p-blog">
      {isPreview && (
        <div className="p-blog_previewMode">
          <Typography.Text fontweight="600" extendClasses="fs-16x20 p-blog_previewMode_text">
            Preview Mode
          </Typography.Text>
        </div>
      )}
      {/* banner */}
      {isLoadingBanner ? <Loading width={50} /> : (
        <Section
          style={{
            backgroundImage: `url(${getResponsiveImage(
              isDesktop,
              isTablet,
              isMobile,
              (bannerData?.translations[localeParams]?.items[0]),
            ) || banner})`,
          }}
          className="p-blog_banner"
        >
          <div className="p-blog_banner_content">
            <div
              className="p-blog_banner_title fs-44x54"
              dangerouslySetInnerHTML={{ __html: bannerData?.translations[localeParams]?.items?.[0]?.data?.title || '' }}
            />
            <div
              className="p-blog_banner_description p-blog_storyHook_description fs-28x33"
              dangerouslySetInnerHTML={{ __html: bannerData?.translations[localeParams]?.items?.[0]?.data?.subTitle || '' }}
            />
          </div>
        </Section>
      )}
      {/* story hook */}
      {blog?.storyHook?.title && (
        <Section className="p-blog_storyHook">
          <div className="p-blog_storyHook_content">
            <div className="p-blog_storyHook_containerImage">
              <div className="p-blog_storyHook_innerImageWrap">
                <img
                  className="p-blog_storyHook_innerImage"
                  src={blog.storyHook.imgSrc || ''}
                  alt="Google"
                />
              </div>
            </div>
            <Image extendClasses="p-blog_storyHook_extendsImage" imgSrc={extendsStoryHook} alt="Google" ratio={1} />
            <div className="p-blog_storyHook_text">
              <Typography.Heading type="h2" extendClasses="p-blog_storyHook_title f-36x48" fontweight="600">
                {blog.storyHook.title}
              </Typography.Heading>
              <div
                className="p-blog_storyHook_description fs-20x24"
                dangerouslySetInnerHTML={{ __html: blog.storyHook.description || '' }}
              />
              {/* <Typography.Text fontweight="400"
              extendClasses="p-blog_storyHook_description fs-20x24">
                {blog.storyHook.description}
              </Typography.Text> */}
              <div className="p-blog_storyHook_containerImageText">
                <Image extendClasses="p-blog_storyHook_extendsImageText" imgSrc={extendsStoryHooks} alt="Google" ratio={1} />
              </div>
            </div>
          </div>
        </Section>
      )}
      {/* problem & solution */}
      {blog?.blocks?.[0]?.title && (
        (
          <Section className="p-blog_problem">
            {blog?.blocks?.map(
              (item, index) => (
                <div key={item.title + index.toString()} className={mapModifiers('p-blog_container', index % 2 === 0 && 'reverse')}>
                  {getFrameBg(index || 0, item.imgSrc)}
                  <div className="p-blog_content">
                    <Typography.Text fontweight="600" extendClasses="p-blog_title fs-32x38">
                      {item.title}
                    </Typography.Text>
                    <div
                      className="p-blog_storyHook_description"
                      dangerouslySetInnerHTML={{ __html: item.description || '' }}
                    />
                    {/* <Typography.Text>
                      {item.description}
                    </Typography.Text> */}
                  </div>
                </div>
              )
            )}
          </Section>
        )
      )}
      {/* Compare section */}
      {blog?.compare?.title && (
        <Section>
          <CompareSection data={blog.compare} />
        </Section>
      )}
      {/* Conclusion */}
      {blog?.conclusion?.title
        && (
          <Section className="p-blog_conclusion">
            <div style={{ backgroundImage: `url(${bgConclusion})` }} className="p-blog_conclusion_wrap">
              <div className="p-blog_conclusion_content">
                <Typography.Heading type="h2" extendClasses="p-blog_conclusion_title f-36x48" fontweight="600">
                  {blog.conclusion.title}
                </Typography.Heading>
                <div
                  className="p-blog_storyHook_description"
                  dangerouslySetInnerHTML={{ __html: blog.conclusion.description || '' }}
                />
                {/* <Typography.Text fontweight="400" extendClasses="p-blog_conclusion_description
               p-blog_storyHook_description fs-20x24">
                  {newsByIdData?.translations[localeParams].metadata.conclusion.description}
                </Typography.Text> */}
              </div>
              <div className="p-blog_conclusion_imageContainer">
                <div className="p-blog_conclusion_imageWrap">
                  <img
                    src={
                      blog.conclusion.imgSrc
                      || chatConclusion
                    }
                    alt={blog.conclusion.imgAlt || ''}
                    className="p-blog_conclusion_image"
                  />
                </div>
              </div>
            </div>
          </Section>
        )}
      {!blog?.conclusion && pathName !== '/blog/hanh-chinh-cong' && (
        <div className="p-blog_relatedArticles-spacing" />
      )}
      {blog?.related
        && blog.related.length > 0 && (
          <Section style={{ paddingTop: 0, paddingBottom: 0 }} className="p-blog_relatedArticles">
            <Section.Title>Explore more stories</Section.Title>
            <div className="p-blog_relatedArticles_carouselWrap">
              <Carousel
                dots={
                  blog.related.length > 3
                  && { className: 'p-blog_relatedArticles_dots' }
                }
                rootClassName={mapModifiers('p-blog_relatedArticles_carousel', blog.related.length < 3 && 'hasNoDots')}
                slidesToShow={3}
                slidesToScroll={3}
                infinite={blog.related.length > 3}
                responsive={[
                  {
                    breakpoint: 1200,
                    settings: { slidesToShow: 2 },
                  },
                  {
                    breakpoint: 768,
                    settings: { slidesToShow: 1 },
                  },
                ]}
              >
                {(blog.related || []).map((item) => (
                  <div key={item.slug} className="p-blog_relatedArticles_itemWrap">
                    <Card
                      onClick={() => navigate(`/blog/${item.slug}`)}
                      style={{ border: 'none', padding: '0!important' }}
                      className="p-blog_relatedArticles_item"
                    >
                      <div className="p-blog_relatedArticles_imageWrap">
                        <img
                          className="p-blog_relatedArticles_image"
                          src={item.imgSrc}
                          alt={item.imgAlt}
                        />
                      </div>
                      <div className="p-blog_relatedArticles_content">
                        <Typography.Text
                          fontweight="600"
                          extendClasses="p-blog_relatedArticles_title fs-20x24"
                        >
                          {item.title}
                        </Typography.Text>
                        <div
                          className="p-blog_relatedArticles_description"
                          dangerouslySetInnerHTML={{
                            __html: truncate(item.description, 100) || '',
                          }}
                        />
                      </div>
                    </Card>
                  </div>
                ))}
              </Carousel>
            </div>
          </Section>
        )}
      {
        pathName !== '/blog/hanh-chinh-cong'
        && (
          <div className="p-home_bannerItem p-blog_bannerExtends">
            <Banner
              backgroundImage={backgroundBanner}
              title="Get started with the Ask2Auto today"
              content={(
                <>
                  Explore our features and share your feedback or
                  <br />
                  questions with a demo meeting.
                </>
              )}
              buttonPrimary={{
                label: 'Book demo now',
                action: () => {
                  setIsOpen(true);
                },
              }}
            />
          </div>
        )
      }

      <ModalRegister isOpen={isOpen} onClose={() => setIsOpen(false)} />
      <FreeTrial isOpen={isOpenFreeModal} onClose={() => setIsOpenFreeModal(false)} />
    </div>
  );
};

export default BlogDetail;
